import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  User,
  Mail,
  Phone,
  Shield,
  Building2,
  Lock,
  Globe,
  Bell,
  Save,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
} from 'lucide-react';
import { useProfile, useUpdateProfile, useChangePassword } from '../../api/queries/profile.queries';

function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { data: profile, isLoading } = useProfile();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();

  // Form states
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [language, setLanguage] = useState<'en' | 'th' | 'zh'>('en');
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    orderAlerts: true,
    inventoryAlerts: true,
  });

  // Password form states
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Update states
  const [profileSaved, setProfileSaved] = useState(false);

  // Load profile data
  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName || profile.username || '');
      setPhone(profile.phone || '');
      setLanguage(profile.preferences?.language || 'en');
      setNotifications({
        email: profile.preferences?.notifications?.email ?? true,
        push: profile.preferences?.notifications?.push ?? true,
        orderAlerts: profile.preferences?.notifications?.orderAlerts ?? true,
        inventoryAlerts: profile.preferences?.notifications?.inventoryAlerts ?? true,
      });
    }
  }, [profile]);

  const handleSaveProfile = async () => {
    try {
      await updateProfile.mutateAsync({
        displayName,
        phone: phone || null,
        preferences: {
          language,
          notifications,
        },
      });

      // Update app language
      if (language !== i18n.language) {
        i18n.changeLanguage(language);
      }

      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch {
      // Error handled by mutation
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError(t('profile.passwordMismatch'));
      return;
    }

    try {
      await changePassword.mutateAsync({
        currentPassword,
        newPassword,
      });

      setPasswordSuccess(t('profile.passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch {
      setPasswordError(t('profile.passwordChangeFailed'));
    }
  };

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'th', label: 'ไทย' },
    { code: 'zh', label: '中文' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold">{t('profile.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('profile.subtitle')}</p>
      </div>

      {/* Success messages */}
      {profileSaved && (
        <div className="mb-6 flex items-center gap-2 p-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg">
          <Check className="h-5 w-5" />
          <span>{t('profile.profileUpdated')}</span>
        </div>
      )}

      {passwordSuccess && (
        <div className="mb-6 flex items-center gap-2 p-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg">
          <Check className="h-5 w-5" />
          <span>{passwordSuccess}</span>
        </div>
      )}

      <div className="space-y-6">
        {/* Account Information */}
        <div className="bg-card rounded-xl border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            {t('profile.accountInfo')}
          </h2>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('profile.displayName')}
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('profile.displayNamePlaceholder')}
                className="w-full px-4 py-2.5 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                {t('profile.email')}
              </label>
              <input
                type="email"
                value={profile?.email || ''}
                disabled
                className="w-full px-4 py-2.5 rounded-lg border bg-muted text-muted-foreground cursor-not-allowed"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                {t('profile.phone')}
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t('profile.phonePlaceholder')}
                className="w-full px-4 py-2.5 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Role (read-only) */}
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground" />
                {t('profile.role')}
              </label>
              <input
                type="text"
                value={profile?.role?.replace(/_/g, ' ') || ''}
                disabled
                className="w-full px-4 py-2.5 rounded-lg border bg-muted text-muted-foreground cursor-not-allowed"
              />
            </div>

            {/* Branch (read-only) */}
            {profile?.branchId && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  {t('profile.branch')}
                </label>
                <input
                  type="text"
                  value={`${profile.branchId.name} (${profile.branchId.code})`}
                  disabled
                  className="w-full px-4 py-2.5 rounded-lg border bg-muted text-muted-foreground cursor-not-allowed"
                />
              </div>
            )}
          </div>
        </div>

        {/* Preferences */}
        <div className="bg-card rounded-xl border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            {t('profile.preferences')}
          </h2>

          <div className="space-y-6">
            {/* Language */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('profile.language')}
              </label>
              <div className="flex gap-3">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code as 'en' | 'th' | 'zh')}
                    className={`px-4 py-2.5 rounded-lg border transition-all ${
                      language === lang.code
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background hover:bg-accent'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Notifications */}
            <div>
              <label className="block text-sm font-medium mb-3 flex items-center gap-2">
                <Bell className="h-4 w-4 text-muted-foreground" />
                {t('profile.notifications')}
              </label>
              <div className="space-y-3">
                {[
                  { key: 'email', label: t('profile.emailNotifications') },
                  { key: 'push', label: t('profile.pushNotifications') },
                  { key: 'orderAlerts', label: t('profile.orderAlerts') },
                  { key: 'inventoryAlerts', label: t('profile.inventoryAlerts') },
                ].map((item) => (
                  <label
                    key={item.key}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={notifications[item.key as keyof typeof notifications]}
                      onChange={(e) =>
                        setNotifications((prev) => ({
                          ...prev,
                          [item.key]: e.target.checked,
                        }))
                      }
                      className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-card rounded-xl border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              {t('profile.changePassword')}
            </h2>
            <button
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              className="text-sm text-primary hover:underline"
            >
              {showPasswordForm ? t('common.cancel') : t('common.edit')}
            </button>
          </div>

          {showPasswordForm && (
            <div className="space-y-4">
              {passwordError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg">
                  <AlertCircle className="h-5 w-5" />
                  <span className="text-sm">{passwordError}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">
                  {t('profile.currentPassword')}
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-2.5 pr-10 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  {t('profile.newPassword')}
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2.5 pr-10 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('profile.passwordRequirements')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  {t('profile.confirmPassword')}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <button
                onClick={handleChangePassword}
                disabled={changePassword.isPending || !currentPassword || !newPassword || !confirmPassword}
                className="px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {changePassword.isPending ? t('common.saving') : t('profile.changePassword')}
              </button>
            </div>
          )}

          {!showPasswordForm && (
            <p className="text-sm text-muted-foreground">
              ********
            </p>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSaveProfile}
            disabled={updateProfile.isPending}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="h-4 w-4" />
            {updateProfile.isPending ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
